<script setup lang="ts">

import { ConditionType, type ICondition } from "@domain/Vehicle";

import ProgressBar from "./ProgressBar.vue";

const model = defineModel<ICondition>( { required: true } );
const emit = defineEmits( [ 'show-flag-tip', 'mark-done' ] );

</script>

<template>

    <!-- INFORMATION ABOUT THE CONDITION -->
    <div>

        <div class="text-center tracking-wider px-4 lg:px-5 2xl:px-8 lg:max-w-[100%] lg:mx-auto 2xl:py-4">

            <!-- CRITICAL -->
            <p v-if="model.isCritical"
                v-html="model.labels.description"
                class="text-sm lg:text-base 3xl:text-xl opacity-60 mt-0.5 pt-1 pb-1">
            </p>

            <!-- DUE AND OVERDUE -->
            <p v-else-if="model.isDue || model.isOverdue"
                class="text-sm lg:text-base 3xl:text-xl pt-1 opacity-60">
                {{ model.labels.actionMessage }}
            </p>

            <!-- INCOMPLETE DATA -->
            <div v-else-if="model.isUnconfigured"
                    class="text-sm lg:text-base 3xl:text-xl pt-1">
                <p class="opacity-60">
                    {{ model.labels.name }} has not been configured yet
                </p>
                <p class="pt-1">
                    <span class="opacity-60">Tap </span>
                    <span class="uppercase text-sm">Set Due Date</span>
                    <span class="opacity-60"> to set it up</span>
                </p>
            </div>

            <!-- UP TO DATE -->
            <div v-else
                    class="text-sm lg:text-base 3xl:text-xl pt-3">
                <p class="opacity-60">
                    Healthy, no action required
                </p>
            </div>

        </div>

        <!-- PROGRESS BAR AND CRITICAL IMAGES -->
        <div class="flex justify-center px-4 lg:px-5">

            <!-- CRITICAL CONDITIONS -->
            <div v-if="model.isCritical" class="flex justify-center pb-10">

                <!-- ITS A RECALL -->
                <div v-if="model.type == ConditionType.Recall"
                        class="h-full flex justify-center items-center pt-3 pb-6">
                    <img src="@/assets/images/conditions/recall.png" class="w-20 lg:w-24 opacity-60" />
                </div>

                <!-- ITS A CRITICAL ISSUE -->
                <div v-else
                        class="h-full flex justify-center items-center pt-4 pb-4">
                    <img src="@/assets/images/health-monitor/carbucks-obd-dark.png" class="w-28 lg:w-32 opacity-60" />
                </div>

                <div class="absolute bottom-4 rounded-2xl h-9 bg-[#902b3a]/10 dark:bg-gray-900
                            flex flex-col gap-2 justify-center items-center
                            tracking-widest uppercase">

                    <div v-if="model.type != ConditionType.Recall"
                            class="indicator-triangle absolute rounded-full bg-error-500 -top-8
                                w-10 h-10 flex items-center justify-center
                                dark:border-[4px] dark:border-gray-900">
                        <span class="text-2xl font-bold text-white">!</span>
                    </div>

                    <div class="px-4 3xl:text-base whitespace-nowrap text-error-500">
                        {{ model.labels.stateLabel }}
                    </div>

                </div>

            </div>

            <!-- ITS A CONDITION WITH INCOMPLETE DATA -->
            <div v-else-if="model.isUnconfigured"
                    class="h-full flex flex-col justify-center items-center pt-4 pb-4 drop-shadow-lg">
                <img src="@/assets/images/conditions/settings.png" class="w-24 lg:w-32 opacity-100 dark:opacity-30 grayscale" />
                <p class="tracking-wider text-gray-500 dark:text-current dark:opacity-60 text-sm 3xl:text-lg -mt-4 -mb-3 text-center pt-4 px-4">
                    You'll earn condition score points<br />when you configure this item!
                </p>

            </div>

            <!-- OTHERWISE ITS A CONDITION WITH A PROGRESS BAR -->
            <div v-else class="w-4/5 lg:w-4/5 2xl:w-2/3 2xl:mt-2 min-w-[300px]">
                <ProgressBar v-model="model"
                                :is-group="false"
                                :key="model.$updateKey" />
            </div>

        </div>

    </div>

</template>
